import { Link } from '@/components/ui'
import { useTranslations } from '@/lib/i18n'
import type { GatewayState } from '@/types'

/**
 * Renders the right banner around gateway data: no merchant assigned, no
 * credentials configured, or a degraded gateway. Returns null when everything
 * is healthy. `manageHref` (admins) links to where credentials are set.
 */
export function GatewayNotice({
  state,
  manageHref,
  createHref,
}: {
  state: GatewayState
  manageHref?: string
  createHref?: string
}) {
  const { t } = useTranslations()

  if (!state.merchant) {
    // Admins (createHref present) get a create CTA; everyone else is told to ask one.
    return (
      <Notice tone="neutral">
        {createHref ? t('gateway_errors.no_merchant_admin') : t('gateway_errors.no_merchant')}
        {createHref && (
          <Link href={createHref} className="ml-2 font-semibold underline">
            {t('gateway_errors.create_merchant')}
          </Link>
        )}
      </Notice>
    )
  }

  if (!state.configured) {
    return (
      <Notice tone="warning">
        {t('gateway_errors.no_credentials')}
        {manageHref && (
          <Link href={manageHref} className="ml-2 font-semibold underline">
            {t('gateway_errors.configure')}
          </Link>
        )}
      </Notice>
    )
  }

  if (state.error) {
    return (
      <Notice tone="failed">
        {state.error.message}
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="ml-2 font-semibold underline"
        >
          {t('gateway_errors.retry')}
        </button>
      </Notice>
    )
  }

  return null
}

const TONES = {
  neutral: 'bg-status-neutral-bg text-status-neutral-fg',
  warning: 'bg-status-warning-bg text-status-warning-fg',
  failed: 'bg-status-failed-bg text-status-failed-fg',
} as const

function Notice({ tone, children }: { tone: keyof typeof TONES; children: React.ReactNode }) {
  return (
    <div role="status" className={`rounded-control px-4 py-3 text-sm ${TONES[tone]}`}>
      {children}
    </div>
  )
}
